import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  changeTriggerStatus,
  createTrigger,
  removeTrigger,
  triggerShellQuery,
  triggersListQuery,
  updateTrigger,
  type TriggerRow,
  type TriggerShell,
} from '../../api/settingsEaseedeskAutoChangeStatus'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Trigger change status.
 *
 * Element cu element fata de `version-3/easeedesk_settings/
 * trigger_change_status/index.php` + `list.php`, si gest cu gest fata de cele
 * doua fisiere din `public/js/views/easeedesk_settings/trigger_change_status/`.
 *
 * Un declansator muta tichetul la un EVENIMENT - plecarea la client, sosirea,
 * anularea - nu dupa un timp. De-aia ecranul n-are cron, si de-aia "activ"
 * inseamna in baza o DATA (`activated_at`), nu o bifa.
 */
const EVENT_LABELS: Record<string, string> = {
  GO_TO_CUSTOMER: 'Go to customer',
  CANCEL_GO_TO_CUSTOMER: 'Cancel go to customer',
  CHECK_IN: 'Check in',
  CHECK_OUT: 'Check out',
  CANCEL_TICKET: 'Cancel ticket',
}

function eventLabel(event: string): string {
  return EVENT_LABELS[event] ?? event.replace(/_/g, ' ')
}

function TriggerRowCells({
  trigger,
  onReload,
  onEdit,
}: {
  trigger: TriggerRow
  onReload(): void
  onEdit(trigger: TriggerRow): void
}) {
  const flip = useMutation({
    mutationFn: (on: boolean) => changeTriggerStatus(trigger.id, on),
    onSuccess: () => {
      toast('Setting changed', 'Success')
      onReload()
    },
    onError: () => {
      toast('Something went wrong.', 'Error', 'error')
      onReload()
    },
  })

  const remove = useMutation({
    mutationFn: () => removeTrigger(trigger.id),
    onSuccess: () => {
      toast('Setting deleted', 'Success')
      onReload()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  return (
    <tr>
      <td className="align-left">
        <span className="label label-default">{eventLabel(trigger.event_type)}</span>
      </td>
      <td className="align-right">{trigger.type_name}</td>
      <td className="align-right">{trigger.status_name}</td>
      <td className="align-right">
        <input
          type="checkbox"
          className="switchery"
          name="activate_trigger"
          checked={trigger.active === 1}
          onChange={(e) => flip.mutate(e.target.checked)}
        />
      </td>
      <td className="align-right">
        <button
          type="button"
          className="btn btn-primary btn-xs edit-trigger"
          data-id={trigger.id}
          onClick={() => onEdit(trigger)}
        >
          <i className="fa fa-edit"></i>
        </button>
      </td>
      <td className="align-right">
        <button
          type="button"
          className="btn btn-danger btn-xs remove-trigger"
          data-id={trigger.id}
          onClick={() => {
            if (!window.confirm('Delete this setting?\nYou will not be able to recover this setting!')) return
            remove.mutate()
          }}
        >
          <i className="fa fa-trash"></i>
        </button>
      </td>
    </tr>
  )
}

export function TriggerChangeStatusPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(triggerShellQuery())
  const list = useQuery(triggersListQuery())
  const [creating, setCreating] = useState(false)
  const [edited, setEdited] = useState<TriggerRow | null>(null)

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'trigger_change_status'] })
  }

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) {
    return <LoadFailed error={shell.error} onRetry={() => void shell.refetch()} what="This screen" />
  }
  if (!shell.data) return <Spinner />

  return (
    <div>
      <h4>
        <strong>Trigger</strong> change status
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Change status <strong>automatically</strong> when an event occured
      </label>
      <button type="button" className="btn btn-success pull-right" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Add settings
      </button>
      <hr />

      <div className="list-group">
        {list.error ? (
          <LoadFailed error={list.error} onRetry={() => void list.refetch()} what="The list" />
        ) : !list.data ? (
          <Spinner />
        ) : (
          <table className="table table-hover">
            <thead>
              <tr>
                <th className="align-left">Event</th>
                <th className="align-right">Ticket type</th>
                <th className="align-right">Change to status</th>
                <th className="align-right">Active</th>
                <th className="align-right">Edit</th>
                <th className="align-right">Remove</th>
              </tr>
            </thead>
            <tbody>
              {list.data.triggers.map((trigger) => (
                <TriggerRowCells key={trigger.id} trigger={trigger} onReload={reload} onEdit={setEdited} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <TriggerModal open={creating} shell={shell.data} onClose={() => setCreating(false)} onSaved={reload} />
      <TriggerModal
        trigger={edited ?? undefined}
        open={edited !== null}
        shell={shell.data}
        onClose={() => setEdited(null)}
        onSaved={reload}
      />
    </div>
  )
}

/** `newSettingsModal` si `editSettingsModal`: acelasi formular cu trei campuri. */
export function TriggerModal({
  trigger,
  open,
  shell,
  onClose,
  onSaved,
}: {
  trigger?: TriggerRow
  open: boolean
  shell: TriggerShell
  onClose(): void
  onSaved(): void
}) {
  const editing = trigger !== undefined
  const [event, setEvent] = useState('')
  const [type, setType] = useState(0)
  const [status, setStatus] = useState(0)

  useEffect(() => {
    if (!open) return
    setEvent(trigger?.event_type ?? '')
    setType(trigger?.ticket_type ?? 0)
    setStatus(trigger?.to_status ?? 0)
  }, [open, trigger])

  const save = useMutation({
    mutationFn: () =>
      editing ? updateTrigger(trigger.id, event, type, status) : createTrigger(event, type, status),
    onSuccess: () => {
      toast(editing ? 'Setting updated' : 'Setting created', 'Success')
      onClose()
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  const incomplete = event === '' || type < 1 || status < 1

  return (
    <Modal
      id={editing ? 'editSettingsModal' : 'newSettingsModal'}
      labelledBy={editing ? 'editSettingsModalLabel' : 'newSettingsModalLabel'}
      open={open}
      onClose={onClose}
      title={editing ? 'Edit settings' : 'New settings'}
    >
      <form
        id={editing ? 'edit-settings-form' : 'new-settings-form'}
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (incomplete) return
          save.mutate()
        }}
      >
        <div className="form-group">
          <label htmlFor="trigger-event" className="control-label">
            Event:
          </label>
          <select
            className="form-control"
            id="trigger-event"
            name="event"
            value={event}
            onChange={(e) => setEvent(e.target.value)}
            required
          >
            <option value="">Select</option>
            {shell.events.map((option) => (
              <option key={option} value={option}>
                {eventLabel(option)}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="trigger-ticket-type" className="control-label">
            Ticket type:
          </label>
          <select
            className="form-control"
            id="trigger-ticket-type"
            name="ticket_type"
            value={type}
            onChange={(e) => setType(Number(e.target.value))}
            required
          >
            <option value={0}>Select</option>
            {shell.ticketTypes.map((t) => (
              <option key={t.ID} value={t.ID}>
                {t.type_name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="trigger-to-status" className="control-label">
            To status:
          </label>
          <select
            className="form-control"
            id="trigger-to-status"
            name="to_status"
            value={status}
            onChange={(e) => setStatus(Number(e.target.value))}
            required
          >
            <option value={0}>Select</option>
            {shell.statuses.map((s) => (
              <option key={s.ID} value={s.ID}>
                {s.status_name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button
            type="submit"
            className={editing ? 'btn btn-primary submit-edit-settings' : 'btn btn-primary submit-new-settings'}
            disabled={incomplete || save.isPending}
          >
            {editing ? 'Save' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
